import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    BarChart3, Users, Package, ShoppingCart, Calendar,
    Settings, DollarSign, Receipt, PieChart, MessageSquare, Truck,
    Tag, User, LogOut, ChevronDown, ChevronRight,
    Shield, Wallet, HeadphonesIcon, UserStarIcon
} from 'lucide-react';
import { useTranslation } from '../i18n/i18n';

const Sidebar = ({ isOpen, onClose, lang, isMobile, collapsed, onToggleCollapse, onLogout, userData }) => {
    const { t } = useTranslation(lang);
    const location = useLocation();
    const [expandedGroups, setExpandedGroups] = useState({});
    const [hoveredItem, setHoveredItem] = useState(null);
    const sidebarWidth = collapsed && !isMobile ? '80px' : '250px';

    const isGroupActive = useCallback((groupItems) => {
        return groupItems.some(item => item.path === location.pathname);
    }, [location.pathname]);

    const menuGroups = [
        {
            id: 'main',
            items: [
                { id: 'dashboard', path: '/dashboard', label: t('dashboard'), icon: BarChart3 }
            ]
        },
        {
            id: 'management',
            label: t('management'),
            icon: Users,
            items: [
                { id: 'users', path: '/users', label: t('users'), icon: User },
                { id: 'customers', path: '/customers', label: t('customers'), icon: UserStarIcon },
                { id: 'suppliers', path: '/suppliers', label: t('suppliers'), icon: Truck },
                { id: 'products', path: '/products', label: t('products'), icon: Package },
                { id: 'promotions', path: '/promotions', label: t('promotions'), icon: Tag },
                { id: 'reservations', path: '/bookings', label: t('reservations'), icon: Calendar }
            ]
        },
        {
            id: 'finance',
            label: t('finance'),
            icon: Wallet,
            items: [
                { id: 'exchange', path: '/exchange', label: t('exchangeRate'), icon: DollarSign },
                { id: 'sales', path: '/sales', label: t('sales'), icon: ShoppingCart },
                { id: 'invoices', path: '/invoices', label: t('invoices'), icon: Receipt },
                { id: 'statistics', path: '/statistics', label: t('statistics'), icon: PieChart }
            ]
        },
        {
            id: 'support',
            label: t('support'),
            icon: HeadphonesIcon,
            items: [
                { id: 'admin-support', path: '/admin-support', label: t('adminSupport'), icon: MessageSquare },
                { id: 'supplier-support', path: '/supplier-support', label: t('supplierSupport'), icon: MessageSquare }
            ]
        },
        {
            id: 'settings',
            items: [
                { id: 'settings', path: '/settings', label: t('settings'), icon: Settings }
            ]
        }
    ];

    const toggleGroup = useCallback((groupId) => {
        setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
    }, []);

    const handleItemClick = useCallback(() => {
        if (window.innerWidth < 1024) {
            onClose();
        }
    }, [onClose]);

    const handleToggleCollapse = useCallback(() => {
        onToggleCollapse();
    }, [onToggleCollapse]);

    return (
        <>
            {/* Overlay pour mobile */}
            {isOpen && (
                <div
                    onClick={onClose}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        overflowX: 'hidden',
                        zIndex: 1000,
                        display: window.innerWidth < 1024 ? 'block' : 'none'
                    }}
                />
            )}

            {/* Sidebar */}
            <aside
                style={{
                    maxWidth: sidebarWidth,
                    minWidth: sidebarWidth,
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    background: 'white',
                    boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    transition: 'all 0.3s ease',
                    transform: isOpen || window.innerWidth >= 1024 ? 'translateX(0)' : 'translateX(-100%)',
                    overflow: 'visible', 
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onMouseLeave={() => setHoveredItem(null)}
            >
                {/* Header */}
                <div style={{
                    padding: collapsed && !isMobile ? '20px 10px' : '20px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                    flexShrink: 0
                }}>
                    <Link 
                        to="/dashboard"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            minWidth: 0,
                            textDecoration: 'none'
                        }}
                        onClick={handleItemClick}
                    >
                        <Shield size={24} color="white" />
                        <h2 style={{
                            display: collapsed && !isMobile ? 'none' : 'block',
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: 'white',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            Admin Panel
                        </h2>
                    </Link>

                    <button
                        onClick={handleToggleCollapse}
                        style={{
                            width: 'max-content',
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        <ChevronRight
                            size={20}
                            style={{
                                transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.3s ease'
                            }}
                        />
                    </button>
                </div>

                {/* Menu Items avec scroll séparé */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '20px 0',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e1 transparent'
                }}>
                    <style>
                        {`
                            .sidebar-scroll::-webkit-scrollbar {
                                width: 6px;
                            }
                            .sidebar-scroll::-webkit-scrollbar-track {
                                background: transparent;
                            }
                            .sidebar-scroll::-webkit-scrollbar-thumb {
                                background: #cbd5e1;
                                border-radius: 3px;
                            }
                            .sidebar-scroll::-webkit-scrollbar-thumb:hover {
                                background: #94a3b8;
                            }
                        `}
                    </style>
                    <nav className="sidebar-scroll">
                        {menuGroups.map(group => (
                            <div key={group.id} style={{ marginBottom: '8px' }}>
                                {/* En-tête de groupe avec icône */}
                                {group.label && (
                                    <div
                                        style={{
                                            position: 'relative'
                                        }}
                                        onMouseEnter={() => setHoveredItem(`group-${group.id}`)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        <button
                                            onClick={() => toggleGroup(group.id)}
                                            style={{
                                                width: '100%',
                                                padding: collapsed && !isMobile ? '12px 10px' : '12px 20px',
                                                background: isGroupActive(group.items) ? '#f5f3ff' : 'transparent',
                                                border: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
                                                cursor: 'pointer',
                                                color: isGroupActive(group.items) ? '#7c3aed' : '#666',
                                                fontSize: '12px',
                                                fontWeight: isGroupActive(group.items) ? '600' : '600',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                position: 'relative'
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: collapsed && !isMobile ? '0' : '12px'
                                            }}>
                                                {group.icon && <group.icon size={16} />}
                                                <span style={{
                                                    display: collapsed && !isMobile ? 'none' : 'inline'
                                                }}>
                                                    {group.label}
                                                </span>
                                            </div>

                                            {/* Toujours afficher les chevrons, même en mode réduit */}
                                            {expandedGroups[group.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        </button>

                                        {/* Tooltip pour les groupes en mode réduit */}
                                        {collapsed && !isMobile && hoveredItem === `group-${group.id}` && (
                                            <div style={{
                                                position: 'absolute',
                                                left: '100%',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: '#1f2937',
                                                color: 'white',
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                whiteSpace: 'nowrap',
                                                zIndex: 1000,
                                                marginLeft: '8px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                            }}>
                                                {group.label}
                                                <div style={{
                                                    position: 'absolute',
                                                    right: '100%',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: 0,
                                                    height: 0,
                                                    borderTop: '6px solid transparent',
                                                    borderBottom: '6px solid transparent',
                                                    borderRight: '6px solid #1f2937'
                                                }} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Items du groupe */}
                                <div style={{
                                    display: group.label && !expandedGroups[group.id] ? 'none' : 'block'
                                }}>
                                    {group.items.map(item => {
                                        const Icon = item.icon;
                                        const isActive = location.pathname === item.path;
                                        return (
                                            <div
                                                key={item.id}
                                                style={{
                                                    position: 'relative'
                                                }}
                                                onMouseEnter={() => setHoveredItem(item.id)}
                                                onMouseLeave={() => setHoveredItem(null)}
                                            >
                                                <Link
                                                    to={item.path} 
                                                    onClick={handleItemClick}
                                                    style={{
                                                        width: '100%',
                                                        padding: collapsed && !isMobile ? '12px 10px' : '12px 20px',
                                                        background: isActive ? '#f5f3ff' : 'transparent',
                                                        border: 'none',
                                                        borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: collapsed && !isMobile ? '0' : '12px',
                                                        cursor: 'pointer',
                                                        color: isActive ? '#7c3aed' : '#666',
                                                        fontSize: '14px',
                                                        fontWeight: isActive ? '600' : '400',
                                                        transition: 'all 0.2s ease',
                                                        textAlign: 'left',
                                                        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                                                        textDecoration: 'none'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isActive) {
                                                            e.currentTarget.style.background = '#fafafa';
                                                            e.currentTarget.style.color = '#7c3aed';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isActive) {
                                                            e.currentTarget.style.background = 'transparent';
                                                            e.currentTarget.style.color = '#666';
                                                        }
                                                    }}
                                                >
                                                    <Icon size={20} />
                                                    <span style={{
                                                        display: collapsed && !isMobile ? 'none' : 'inline'
                                                    }}>
                                                        {item.label}
                                                    </span>
                                                </Link>

                                                {/* Tooltip pour les items en mode réduit */}
                                                {collapsed && !isMobile && hoveredItem === item.id && (
                                                    <div style={{
                                                        position: 'fixed',
                                                        left: `${sidebarWidth}`,
                                                        top: 'auto',
                                                        transform: 'none',
                                                        background: '#1f2937',
                                                        color: 'white',
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: '500',
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 10000,
                                                        marginLeft: '8px',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                        pointerEvents: 'none'
                                                    }}>
                                                        {item.label}
                                                        <div style={{
                                                            position: 'absolute',
                                                            right: '100%',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderTop: '6px solid transparent',
                                                            borderBottom: '6px solid transparent',
                                                            borderRight: '6px solid #1f2937'
                                                        }} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>
                </div>

                {/* Footer */}
                <div style={{
                    padding: collapsed && !isMobile ? '20px 10px' : '20px',
                    borderTop: '1px solid #f0f0f0',
                    flexShrink: 0
                }}>
                    {/* Optionnel : Afficher les infos user */}
                    {userData && !collapsed && (
                    <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: '8px'
                    }}>
                        <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#1f2937',
                        marginBottom: '2px'
                        }}>
                        {userData.email}
                        </div>
                        <div style={{
                        fontSize: '11px',
                        color: '#6b7280'
                        }}>
                        {userData.role?.toUpperCase() || 'no role'}
                        </div>
                    </div>
                    )}
                    
                    <button
                    onClick={onLogout} 
                    style={{
                        width: '100%',
                        padding: '12px',
                        background: '#fee',
                        border: 'none',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: collapsed && !isMobile ? '0' : '8px',
                        cursor: 'pointer',
                        color: '#dc2626',
                        fontSize: '14px',
                        fontWeight: '500',
                        minHeight: '44px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fecaca';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fee';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    >
                    <LogOut size={18} />
                    <span style={{
                        display: collapsed && !isMobile ? 'none' : 'inline'
                    }}>
                        {t('logout')}
                    </span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;